import {Action, Character} from "@/characters/model/Character.ts";
import ActionView from "@/characters/admin/details/ActionView.tsx";
import SpellcastingView from "@/characters/admin/details/SpellcastingView.tsx";
import DetailSection from "@/components/details/DetailSection.tsx";
import CharacterDetailsHeader from "@/characters/admin/details/CharacterDetailsHeader.tsx";
import DetailPortrait from "@/components/details/DetailPortrait.tsx";
import CharacterStats from "@/characters/admin/details/CharacterStats.tsx";
import {useCharacterDetailsViewModel} from "@/characters/admin/details/useCharacterDetailsViewModel.ts";
import {cn} from "@/lib/utils.ts";
import {HorizontalLine} from "@/components/HorizontalLine.tsx";

export default function CharacterDetails({character}: { character: Character }) {
    const {actions, traits, reactions, legendaryActions, detailRows} = useCharacterDetailsViewModel(character);
    const legendaryInfo = `Der ${character.type ?? "Charakter"} kann 3 legendäre Aktionen durchführen, wobei er aus den beschriebenen Optionen auswählt. Er kann nur eine legendäre Option auf einmal und nur am Ende des Zugs eines anderen Charakters verwenden. Verbrauchte legendäre Aktionen werden zu Beginn seines Zuges zurückgewonnen.`;

    return (
        <article className={cn(
            "@container overflow-hidden rounded-xl border bg-linear-to-br to-surface text-sm text-foreground shadow-xl shadow-black/25",
            character.hostile
                ? "border-hostile/50 from-hostile-dark/35"
                : "border-friendly/50 from-friendly-dark/35",
        )}>
            <CharacterDetailsHeader character={character}/>

            <div className={cn(
                "grid",
                character.image && "@min-[48rem]:grid-cols-[minmax(0,2fr)_minmax(13rem,1fr)]",
            )}>
                {character.image &&
                    <DetailPortrait image={character.image} alt={`Porträt von ${character.name}`}/>}

                <div className="min-w-0 @min-[48rem]:col-start-1 @min-[48rem]:row-start-1">
                    <CharacterStats character={character} detailRows={detailRows}/>

                    {character.spellcasting && <SpellcastingView spellcasting={character.spellcasting}/>}

                    <ActionSection title="Merkmale" actions={traits}/>
                    <ActionSection title="Aktionen" actions={actions}/>
                    <ActionSection title="Reaktionen" actions={reactions}/>
                    <ActionSection title="Legendär" actions={legendaryActions} info={legendaryInfo}/>
                </div>
            </div>
        </article>
    );
}

function ActionSection({title, actions, info}: { title: string; actions: Action[]; info?: string }) {
    if (actions.length === 0) return null;

    return <DetailSection title={title} count={actions.length} info={info}>
        <HorizontalLine/>
        <div className="flex flex-col mt-2 gap-1.5">
            {actions.map((action, index) =>
                <ActionView key={`${action.name}-${index}`} action={action}/>)}
        </div>
    </DetailSection>;
}
